import { z } from 'zod';
import { foreignNormSchema, type ForeignNorm } from './requests.js';
import type { UserRef } from './tasks.js';
import { FILE_KINDS, type UploadFileKind } from './workflow.js';

export const FILE_KIND_LABELS = {
  document: 'Документ',
  scan: 'Скан',
  attachment: 'Вложение',
  approval_sheet: 'Лист согласования',
  generated: 'Сформирован по шаблону',
} as const;
export type FileKind = keyof typeof FILE_KIND_LABELS;

/**
 * Как выбрать вид при загрузке: что это, примеры и чем вид отличается для системы.
 * Показывается под выбором вида и в памятке «Какой вид выбрать?».
 */
export const FILE_KIND_HINTS: Record<UploadFileKind, { what: string; examples: string; effect: string }> = {
  document: {
    what: 'то, что согласуют',
    examples: 'договор, приказ, служебная записка, положение',
    effect: 'попадает в лист согласования с номером версии — видно, какой именно текст согласовали',
  },
  attachment: {
    what: 'для справки, само не согласуется',
    examples: 'счёт поставщика, коммерческое предложение, расчёт, переписка, фото',
    effect: 'в лист согласования не попадает; маршрут может требовать вложение — например, счёт к заявке на оплату',
  },
  scan: {
    what: 'подписанный бумажный экземпляр',
    examples: 'скан договора с подписями и печатями, подписанного приказа',
    effect: 'единственный вид, который можно добавить после согласования',
  },
};

/** Разрешённые расширения. Содержимое файла дополнительно сверяется с расширением на сервере. */
export const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'ods', 'rtf', 'txt', 'csv', 'png', 'jpg', 'jpeg'] as const;

/** Сменить вид уже загруженного файла: документ, скан подписанного, вложение. */
export const fileKindSchema = z.object({ kind: z.enum(FILE_KINDS) });

export const uploadFileSchema = z.object({
  kind: z.enum(FILE_KINDS).default('document'),
  comment: z.string().trim().max(1000).optional(),
});

export const uploadVersionSchema = z.object({
  /** Версия, которую пользователь скачал и правил. */
  basedOnVersion: z.coerce.number().int().min(1),
  comment: z.string().trim().max(1000).optional(),
});

export const editMarkSchema = z.object({ editing: z.boolean() });

export interface EditMarkDto {
  user: UserRef;
  since: string;
  expiresAt: string;
  baseVersion: number;
}

export interface FileVersionDto {
  version: number;
  basedOnVersion: number | null;
  originalName: string;
  size: number;
  mime: string;
  comment: string | null;
  uploadedBy: UserRef | null;
  createdAt: string;
  preview: 'none' | 'pending' | 'ready' | 'failed' | 'native';
}

export interface FileDto {
  id: string;
  kind: FileKind;
  name: string;
  currentVersion: number;
  createdAt: string;
  updatedAt: string;
  versions: FileVersionDto[];
  editing: EditMarkDto[];
  canUpload: boolean;
  canDelete: boolean;
  /** Можно сменить вид (документ / скан / вложение), не удаляя файл. */
  canChangeKind: boolean;
}

export interface CheckoutDto {
  /** Кто ещё сейчас правит файл — показать предупреждение. */
  othersEditing: EditMarkDto[];
  version: number;
}

export interface SettingsDto {
  'files.notify_approvers_on_new_version': boolean;
  'files.notify_initiator_on_new_version': boolean;
  /** Суточные в командировке по России, ₽ в день. */
  'trips.per_diem_rate': number;
  /** Проживание по России, ₽ в сутки (ночей на одну меньше, чем дней). */
  'trips.lodging_rate': number;
  /**
   * Нормы для заграничных командировок в долларах США: суточные — Приложение N 1
   * к ПП РФ от 26.12.2005 N 812 (1-й столбец), проживание — приложение к ПП РФ от 22.08.2020 N 1267.
   */
  'trips.foreign_norms': ForeignNorm[];
  /** Курсы ЦБ РФ: ₽ за 1 единицу валюты (USD, EUR, …), и дата, на которую они установлены. */
  'trips.currency_rates': Record<string, number>;
  'trips.rates_date': string | null;
  /** Обновлять курсы с сайта ЦБ РФ автоматически. */
  'trips.rates_auto': boolean;
  /** За границей суточные: по нормативу страны или по единому тарифу (₽ в день). */
  'trips.foreign_per_diem_mode': 'norm' | 'flat';
  'trips.foreign_per_diem_rate': number;
  /** За границей проживание: по нормативу страны или по единому тарифу (₽ в сутки). */
  'trips.foreign_lodging_mode': 'norm' | 'flat';
  'trips.foreign_lodging_rate': number;
  'reminders.enabled': boolean;
  /** За сколько часов до срока напомнить. */
  'reminders.due_soon_hours': number;
  /** Через сколько часов без решения напоминать (для шагов без срока). */
  'reminders.pending_after_hours': number;
  /** В котором часу отправлять сводку тем, кто сегодня не заходил, а задачи ждут. */
  'reminders.digest_hour': number;
}

export const settingsSchema = z
  .object({
    'files.notify_approvers_on_new_version': z.boolean(),
    'files.notify_initiator_on_new_version': z.boolean(),
    'trips.per_diem_rate': z.number().min(0).max(100_000),
    'trips.lodging_rate': z.number().min(0).max(1_000_000),
    'trips.foreign_norms': z.array(foreignNormSchema).max(1000),
    'trips.currency_rates': z.record(z.string().regex(/^[A-Z]{3}$/), z.number().positive().max(1_000_000)),
    'trips.rates_date': z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Укажите дату').nullable(),
    'trips.rates_auto': z.boolean(),
    'trips.foreign_per_diem_mode': z.enum(['norm', 'flat']),
    'trips.foreign_per_diem_rate': z.number().min(0).max(1_000_000),
    'trips.foreign_lodging_mode': z.enum(['norm', 'flat']),
    'trips.foreign_lodging_rate': z.number().min(0).max(10_000_000),
    'reminders.enabled': z.boolean(),
    'reminders.due_soon_hours': z.number().int().min(1).max(240),
    'reminders.pending_after_hours': z.number().int().min(1).max(720),
    'reminders.digest_hour': z.number().int().min(0).max(23),
  })
  .partial();

export interface LaunchCheckResultDto {
  label: string;
  ok: boolean;
  message: string | null;
  /** Где исправлять: в «Файлах» (файл нужного вида) или в поле заявки (связанный документ). */
  target: { type: 'file'; kind: FileKind } | { type: 'field'; field: string } | null;
}
